import { AppConfig, PartialAppConfig } from '@dh-io-emh/config-types';
import { defineCustomElements as defineCustomElementsFootnotes } from '@dh-io-emh/footnotes/dist/loader';
import { defineCustomElements as defineCustomElementsPriceDisplay } from '@dh-io-pls/price-display/dist/loader';
import { defineCustomElements as defineCustomElementsSaveButton } from '@mm-consid/save-button/dist/loader';
import { defineCustomElements as defineCustomElementsFcisWidget } from 'fcis-widget/dist/loader';
import { defineCustomElements as defineCustomWebComponentLoaderElement } from '@owc/web-component-loader/loader';
import merge from 'lodash/merge';
import { App } from 'vue';
import { configApi } from '../api/config.api';
import { setupAxiosAuthInterceptors } from '../auth/axios-interceptors';
import { initializeDealerContext } from '../context/dealer.context';
import { infuse, initContainer } from '../ioc-config';
import { AbstractRouterOptions } from '../router/types';
import { initializeStores } from '../stores';
import { initBroadcaster } from '../utils/broadcaster.util';
import { initDataDog } from '../utils/datadog.util';
import { initLogger } from '../utils/logger.util';

export const setupAppInstance = async (
  prefix: string,
  appConfig: AppConfig,
  routerOptions: AbstractRouterOptions,
  app: App,
) => {
  await initContainer(appConfig, routerOptions, app);
  await initializeStores();
  await initializeDealerContext();
  initDataDog(prefix, appConfig.dataDog, appConfig.shopCountry, appConfig.locale);
  initLogger(prefix, appConfig.logLevel);
  initBroadcaster(prefix);
  installComponents();
  await initAuth();
};

export const getConfigUrl = (env: string, country: string) => {
  const lcEnv = env.toLowerCase();
  const lcCountry = country.toLowerCase();

  return lcEnv === 'prod'
    ? `https://assets.oneweb.mercedes-benz.com/plugin/emh-dcps-mrktplc-vehicles-configuration/latest/${lcEnv}/config_${lcCountry}.json`
    : `https://int.assets.oneweb.mercedes-benz.com/plugin/emh-dcps-mrktplc-vehicles-configuration/latest/${lcEnv}/config_${lcCountry}.json`;
};

export const loadAppConfig = async (
  environment: string,
  country: string,
  language: string,
  customConfig?: PartialAppConfig,
): Promise<AppConfig> => {
  const configUrl = getConfigUrl(environment, country);

  const config: AppConfig = await loadFrontendConfig(configUrl, country, language);

  return merge(config, await loadBackendConfig(config), customConfig);
};

const initAuth = async () => {
  const { getAuthState } = infuse('authService');
  const { isAuthenticated, jweToken } = await getAuthState();

  if (isAuthenticated) {
    setupAxiosAuthInterceptors(jweToken as string);
  }
};

const loadFrontendConfig = (configUrl: string, country: string, language: string): Promise<AppConfig> =>
  configApi.getAppConfig(configUrl, country, language);

const loadBackendConfig = async ({ apiBaseUrl, siteId }: AppConfig): Promise<AppConfig> =>
  configApi.getHybrisConfig(`${apiBaseUrl}/v2/${siteId}/config`);

export const installComponents = () => {
  defineCustomElementsFootnotes(window);
  defineCustomElementsPriceDisplay(window);
  defineCustomElementsFcisWidget(window);
  defineCustomElementsSaveButton(window);
  defineCustomWebComponentLoaderElement(window);
};
